import { useState } from "react";
import { guideSteps, settingItems } from "@/mocks/comicGuide";

export default function ReadingGuide() {
  const [mode, setMode] = useState<"scroll" | "page">("scroll");
  const [night, setNight] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <section id="guide" className="w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs text-primary-700">
            <i className="ri-settings-3-line text-sm"></i>
            阅读设置
          </span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl text-foreground-900">
            新手也能一读就懂
          </h2>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600">
            跟着下面的步骤走，几分钟内就能把阅读模式、字号、亮度全部调好，顺畅看完全篇。
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-start">
          {/* Step list */}
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-foreground-900">
              <i className="ri-list-check-2 text-primary-500"></i>
              六步快速上手
            </h3>
            <ol className="mt-6 space-y-6 border-l-2 border-primary-200 pl-6">
              {guideSteps.map((step, i) => (
                <li key={step.title} className="relative">
                  <span className="absolute -left-6 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                    {i + 1}
                  </span>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className={`${step.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h4 className="font-semibold text-foreground-900">{step.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Interactive demo panel */}
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h3 className="flex items-center gap-2 text-lg font-bold text-foreground-900">
              <i className="ri-tools-line text-primary-500"></i>
              阅读设置演示（可交互）
            </h3>
            <p className="mt-1 text-sm text-foreground-600">试着切换下面的选项，看看实际效果。</p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {settingItems.slice(0, 4).map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 rounded-md bg-background-50 border border-background-200 px-4 py-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900">{item.label}</p>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* mode toggle */}
            <div className="mt-5 rounded-md bg-background-50 border border-background-200 p-4">
              <p className="text-sm font-medium text-foreground-900">阅读模式</p>
              <div className="mt-3 grid grid-cols-2 gap-1 rounded-full bg-secondary-100 p-1">
                <button
                  type="button"
                  onClick={() => setMode("scroll")}
                  className={`rounded-full px-1 py-2 text-sm transition-colors ${
                    mode === "scroll"
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-secondary-200/60"
                  }`}
                >
                  下拉
                </button>
                <button
                  type="button"
                  onClick={() => setMode("page")}
                  className={`rounded-full px-1 py-2 text-sm transition-colors ${
                    mode === "page"
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-secondary-200/60"
                  }`}
                >
                  翻页
                </button>
              </div>
            </div>

            {/* toggles */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setNight((v) => !v)}
                className="flex items-center justify-between rounded-md bg-background-50 border border-background-200 px-4 py-3 text-sm text-foreground-800 hover:border-primary-300"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-moon-line text-primary-500"></i>
                  夜间模式
                </span>
                <span
                  className={`flex h-6 w-11 items-center rounded-full p-0.5 transition-colors ${
                    night ? "bg-primary-500" : "bg-secondary-200"
                  }`}
                >
                  <span
                    className={`h-5 w-5 rounded-full bg-background-50 transition-transform ${
                      night ? "translate-x-5" : ""
                    }`}
                  ></span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setFullscreen((v) => !v)}
                className="flex items-center justify-between rounded-md bg-background-50 border border-background-200 px-4 py-3 text-sm text-foreground-800 hover:border-primary-300"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-fullscreen-line text-primary-500"></i>
                  全屏阅读
                </span>
                <i
                  className={`text-lg ${fullscreen ? "ri-checkbox-circle-fill text-primary-500" : "ri-checkbox-circle-line text-secondary-400"}`}
                ></i>
              </button>
            </div>

            {/* preview */}
            <div
              className={`mt-4 rounded-md border px-4 py-5 text-center transition-colors ${
                night ? "bg-foreground-950 border-foreground-800" : "bg-background-50 border-background-200"
              }`}
            >
              <p className={`text-sm ${night ? "text-background-100" : "text-foreground-600"}`}>
                {mode === "scroll"
                  ? "下拉模式：画面连续向下滚动，追更一气呵成。"
                  : "翻页模式：左右滑动翻页，保留纸质漫画手感。"}
              </p>
              {fullscreen && (
                <p className={`mt-2 text-xs ${night ? "text-accent-400" : "text-primary-600"}`}>
                  <i className="ri-fullscreen-exit-line mr-1"></i>已开启全屏沉浸阅读
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}